import type { ShopKind } from './ShopKind';

/**
 * Shared spatial constants for the station interior. Anything that spans
 * multiple builders (architecture, alcoves, hangar, scene) reads from here
 * so layout changes only touch one file.
 */

export const INTERIOR_RADIUS = 44;
export const ATRIUM_RADIUS = 10;
export const CEILING_HEIGHT = 22;
export const DOME_HEIGHT = 14;
export const FLOOR_Y = 0;

/** Radius at which shop counters anchor (inside the alcove mouth). */
export const SHOP_COUNTER_RADIUS = INTERIOR_RADIUS * 0.6;
/** Depth of each alcove pocket into the outer wall. */
export const ALCOVE_DEPTH = 10;
/** Half-angle (radians) of each alcove mouth. */
export const ALCOVE_HALF_ANGLE = Math.PI * 0.09; // ~16°

/** Player spawns next to their docked ship in the hangar, facing the atrium. */
export const PLAYER_SPAWN_X = 4.8;
export const PLAYER_SPAWN_Z = INTERIOR_RADIUS * 0.62; // matches PLAYER_SHIP_DOCK_Z
export const PLAYER_SPAWN_FACING = 0; // local -Z points to world -Z → look toward atrium center

/** Hangar parameters. */
export const HANGAR_CENTER_Z = INTERIOR_RADIUS * 0.72;
export const HANGAR_ARC = Math.PI / 2.5; // ~72° wide window arch
export const HANGAR_FLOOR_Y = 0;
export const HANGAR_PLATFORM_RAISE = 0.55;
export const EXIT_PAD_Z = INTERIOR_RADIUS * 0.48;
export const EXIT_PAD_RADIUS = 2.4;
export const PLAYER_SHIP_DOCK_Z = INTERIOR_RADIUS * 0.62;
export const NPC_DOCK_OFFSET = 14; // lateral offset of NPC ship docks

/**
 * Shop angles (radians, 0 = +Z = hangar). Hangar takes the +Z arc, so the
 * four shops live on the sides and the far (-Z) side. Distributed in a way
 * that each has breathing room and the cardinal paths remain clear.
 */
export const SHOP_ANGLES: Record<ShopKind, number> = {
  'ship-upgrade': Math.PI * (0.33), // NE-ish
  'trade-terminal': Math.PI * (0.67), // SE-ish
  'weapon-tool': Math.PI * (1.33), // SW-ish
  'suit-upgrade': Math.PI * (1.67) // NW-ish
};

/** For pathways on the floor: each shop gets a radial line; hangar gets a wider band. */
export const PATHWAY_WIDTH = 1.6;
export const PATHWAY_INNER = ATRIUM_RADIUS + 0.3;
export const PATHWAY_OUTER = INTERIOR_RADIUS - 2.5;

export function polar(angle: number, radius: number): { x: number; z: number } {
  return {
    x: Math.sin(angle) * radius,
    z: Math.cos(angle) * radius
  };
}
